// packages/ui/src/Alert.tsx
import React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  type LucideIcon,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Info,
  X,
} from 'lucide-react';
import { IconButton } from './IconButton';

export type AlertType = 'success' | 'error' | 'warning' | 'info';

export interface AlertProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'onDrag' | 'onDragEnd' | 'onDragEnter' | 'onDragExit' | 'onDragLeave' | 'onDragOver' | 'onDragStart' | 'onDrop' |
  'onMouseDown' | 'onMouseMove' | 'onMouseUp' | 'onTouchStart' | 'onTouchMove' | 'onTouchEnd' |
  'onClick' | 'onAnimationStart' | 'onAnimationEnd' | 'onAnimationIteration'
> {
  type: AlertType;
  title?: string;
  titleKey?: string;
  message?: string | React.ReactNode;
  messageKey?: string;
  onClose?: () => void;
  className?: string;
  titleClassName?: string;
  messageClassName?: string;
  isVisible?: boolean;
  actionButton?: React.ReactNode; // Your added prop
  icon?: React.ReactNode; // FIX: Added optional custom icon prop to resolve error
}

export const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  ({
    type,
    title,
    titleKey,
    message,
    messageKey,
    onClose,
    className,
    titleClassName,
    messageClassName,
    isVisible = true,
    actionButton,
    icon, // FIX: Destructure new prop
    ...props
  }, ref) => {
    const { t } = useTranslation();

    const resolvedTitle = titleKey ? t(titleKey) : title;
    const resolvedMessage = messageKey ? t(messageKey) : message;

    // FIX: Use custom icon if provided, else fallback to internal
    const defaultIconByType: Record<AlertType, LucideIcon> = {
      success: CheckCircle,
      error: XCircle,
      warning: AlertTriangle,
      info: Info,
    };
    const fallbackIcon = defaultIconByType[type];

    const renderedIcon = React.isValidElement(icon)
      ? icon
      : React.createElement(fallbackIcon, { size: 20, 'aria-hidden': true });

    const typeClasses = {
      success: {
        bg: 'bg-emerald-50 [.theme-liquid-glass_&]:bg-emerald-500/10',
        border: 'border-emerald-500 [.theme-liquid-glass_&]:border-emerald-400/40',
        icon: 'text-emerald-500 [.theme-liquid-glass_&]:text-emerald-300',
        title: 'text-emerald-800 [.theme-liquid-glass_&]:text-emerald-100',
        message: 'text-emerald-700 [.theme-liquid-glass_&]:text-emerald-100/80',
      },
      error: {
        bg: 'bg-red-50 [.theme-liquid-glass_&]:bg-rose-500/10',
        border: 'border-red-500 [.theme-liquid-glass_&]:border-rose-400/40',
        icon: 'text-red-500 [.theme-liquid-glass_&]:text-rose-300',
        title: 'text-red-800 [.theme-liquid-glass_&]:text-rose-100',
        message: 'text-red-700 [.theme-liquid-glass_&]:text-rose-100/80',
      },
      warning: {
        bg: 'bg-amber-50 [.theme-liquid-glass_&]:bg-amber-500/10',
        border: 'border-amber-500 [.theme-liquid-glass_&]:border-amber-400/40',
        icon: 'text-amber-500 [.theme-liquid-glass_&]:text-amber-300',
        title: 'text-amber-800 [.theme-liquid-glass_&]:text-amber-100',
        message: 'text-amber-700 [.theme-liquid-glass_&]:text-amber-100/80',
      },
      info: {
        bg: 'bg-sky-50 [.theme-liquid-glass_&]:bg-sky-500/10',
        border: 'border-sky-500 [.theme-liquid-glass_&]:border-sky-400/40',
        icon: 'text-sky-500 [.theme-liquid-glass_&]:text-sky-300',
        title: 'text-sky-800 [.theme-liquid-glass_&]:text-sky-100',
        message: 'text-sky-700 [.theme-liquid-glass_&]:text-sky-100/80',
      },
    };

    const currentTypeClasses = typeClasses[type];

    const ariaRole = {
      success: 'status',
      info: 'status',
      warning: 'alert',
      error: 'alert',
    }[type];

    return (
      <AnimatePresence>
        {isVisible && (
          <motion.div
            ref={ref}
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className={clsx(
              // Base structure
              'relative p-4 flex items-start gap-4',

              // Neobrutalism
              'rounded-xl border-2 border-black',

              // Liquid glass overrides
              '[.theme-liquid-glass_&]:rounded-2xl [.theme-liquid-glass_&]:border [.theme-liquid-glass_&]:border-white/20',
              '[.theme-liquid-glass_&]:backdrop-blur-xl',

              currentTypeClasses.bg,
              currentTypeClasses.border,
              className
            )}
            role={ariaRole}
            {...props}
          >
            <div className={clsx('flex-shrink-0', currentTypeClasses.icon)}>
              {renderedIcon}
            </div>
            <div className="flex-grow">
              {resolvedTitle && (
                <h3 className={clsx('font-bold text-lg leading-tight mb-0.5', currentTypeClasses.title, titleClassName)}>
                  {resolvedTitle}
                </h3>
              )}
              {resolvedMessage && (
                <div className={clsx('text-sm', currentTypeClasses.message, messageClassName)}>
                  {resolvedMessage}
                </div>
              )}
            </div>
            <div className="flex-shrink-0 ml-auto flex items-center gap-2">
              {actionButton && <div className="-mt-1">{actionButton}</div>}
              {onClose && (
                <div className="-mt-1 -mr-1">
                  <IconButton
                    icon={<X size={18} />}
                    onClick={onClose}
                    ariaLabel={t('common.close', 'Close')}
                    variant="ghost"
                    size="sm"
                    className={clsx('!border-transparent', currentTypeClasses.icon)}
                    applyNeobrutalistEffects={false}
                  />
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }
);

Alert.displayName = 'Alert';
